import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faShoePrints, faXmark } from '@fortawesome/free-solid-svg-icons';
import InsoleIllustration from './InsoleIllustration';
import './WhatAreInsoles.css';

const WhatAreInsoles = () => {
    const { t } = useTranslation();
    const descriptionTitle = t('insoles.description_title', { defaultValue: '' });
    const descriptionItems = t('insoles.description_items', { returnObjects: true, defaultValue: [] });
    const hasDescriptionList = Array.isArray(descriptionItems) && descriptionItems.length > 0;

    const problems = [
        { key: 'flat_feet', color: 'primary' },
        { key: 'heel_spur', color: 'error' },
        { key: 'diabetic_ulcers', color: 'warning' },
        { key: 'scoliosis', color: 'info' },
        { key: 'arthrosis', color: 'accent' },
        { key: 'leg_length_discrepancy', color: 'secondary' },
        { key: 'foot_joint_deformities', color: 'primary' },
        { key: 'posture_disorder', color: 'secondary' },
        { key: 'leg_fatigue', color: 'warning' },
        { key: 'knee_pain', color: 'error' },
        { key: 'x_o_deformity', color: 'accent' },
    ];

    const row1Problems = problems.slice(0, 4);
    const row2Problems = problems.slice(4, 8);
    const row3Problems = problems.slice(8, 11);

    return (
        <section id="insoles" className="section insoles-section">
            <div className="container">
                {/* Header */}
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="section-title">{t('insoles.title')}</h2>
                    <p className="section-subtitle">{t('insoles.subtitle')}</p>
                </motion.div>

                {/* Main Content */}
                <div className="insoles-content">
                    {/* Description Card */}
                    <motion.div
                        className="insoles-main-card"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="main-card-visual" aria-hidden="true">
                            <img src="/logo.jpg" alt="Podiatech logo" className="main-card-logo" />
                        </div>
                        <div className="main-card-copy">
                            {hasDescriptionList ? (
                                <>
                                    <p className="main-card-title">{descriptionTitle}</p>
                                    <ul className="main-card-list">
                                        {descriptionItems.map((item, index) => (
                                            <li key={`${item}-${index}`} className="main-card-list-item">
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <p className="main-card-text">{t('insoles.description')}</p>
                            )}
                        </div>
                    </motion.div>

                    {/* Problems Carousel */}
                    <div className="problems-section">
                        <h3 className="problems-title">{t('insoles.problems_title')}</h3>

                        <div className="problems-carousel-container">
                            <div className="problems-fade-mask">
                                <motion.div
                                    className="problems-carousel-row"
                                    animate={{ x: ['0%', '-50%'] }}
                                    transition={{
                                        ease: 'linear',
                                        duration: 35,
                                        repeat: Infinity,
                                    }}
                                >
                                    {[...row1Problems, ...row1Problems, ...row1Problems].map((problem, i) => (
                                        <div
                                            key={`row1-${i}`}
                                            className={`problem-card problem-${problem.color}`}
                                        >
                                            <div className="problem-media" aria-hidden="true">
                                                <InsoleIllustration variant={problem.key} tone={problem.color} />
                                            </div>
                                            <span className="problem-text">{t(`insoles.problems.${problem.key}`)}</span>
                                        </div>
                                    ))}
                                </motion.div>

                                <motion.div
                                    className="problems-carousel-row"
                                    animate={{ x: ['-50%', '0%'] }}
                                    transition={{
                                        ease: 'linear',
                                        duration: 40,
                                        repeat: Infinity,
                                    }}
                                    style={{ marginTop: '0.75rem' }}
                                >
                                    {[...row2Problems, ...row2Problems, ...row2Problems].map((problem, i) => (
                                        <div
                                            key={`row2-${i}`}
                                            className={`problem-card problem-${problem.color}`}
                                        >
                                            <div className="problem-media" aria-hidden="true">
                                                <InsoleIllustration variant={problem.key} tone={problem.color} />
                                            </div>
                                            <span className="problem-text">{t(`insoles.problems.${problem.key}`)}</span>
                                        </div>
                                    ))}
                                </motion.div>

                                <motion.div
                                    className="problems-carousel-row"
                                    animate={{ x: ['0%', '-50%'] }}
                                    transition={{
                                        ease: 'linear',
                                        duration: 35,
                                        repeat: Infinity,
                                    }}
                                    style={{ marginTop: '0.75rem' }}
                                >
                                    {[...row3Problems, ...row3Problems, ...row3Problems].map((problem, i) => (
                                        <div
                                            key={`row3-${i}`}
                                            className={`problem-card problem-${problem.color}`}
                                        >
                                            <div className="problem-media" aria-hidden="true">
                                                <InsoleIllustration variant={problem.key} tone={problem.color} />
                                            </div>
                                            <span className="problem-text">{t(`insoles.problems.${problem.key}`)}</span>
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comparison Section */}
                <motion.div
                    className="comparison-section"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <h3 className="comparison-title">{t('insoles.why_individual')}</h3>
                    <div className="comparison-grid">
                        {/* Standard */}
                        <div className="comparison-card comparison-standard">
                            <div className="comparison-card-visual">
                                {/* <InsoleIllustration variant="standard" tone="error" /> */}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="rgb(239 68 68)" d="M328 256C306.9 243.9 285.7 231.8 256 226.7L256 86.4C289.7 77 343.4 64 384 64C480 64 608 112 608 192C608 272 488.4 288 432 288C384 288 356 272 328 256zM160 96L208 96L208 224L160 224C124.7 224 96 195.3 96 160C96 124.7 124.7 96 160 96zM264 384C292 368 320 352 368 352C424.4 352 544 368 544 448C544 528 416 576 320 576C279.5 576 225.7 563 192 553.6L192 413.3C221.7 408.1 242.9 396 264 383.9zM96 544C60.7 544 32 515.3 32 480C32 444.7 60.7 416 96 416L144 416L144 544L96 544z"/></svg>

                                <div className="comparison-badge standard not-checked" aria-hidden="true">
                                    <FontAwesomeIcon icon={faXmark} />
                                </div>
                            </div>
                            <h4 className="comparison-card-title">{t('insoles.comparison.standard')}</h4>
                            <p className="comparison-card-desc">{t('insoles.comparison.standard_desc')}</p>
                        </div>

                        {/* VS */}
                        <div className="comparison-vs">VS</div>

                        {/* Individual */}
                        <div className="comparison-card comparison-individual">
                            <div className="comparison-card-visual">
                                <InsoleIllustration variant="individual" tone="secondary" />
                                <div className="comparison-badge individual" aria-hidden="true">
                                    <FontAwesomeIcon icon={faCheck} />
                                </div>
                            </div>
                            <h4 className="comparison-card-title">{t('insoles.comparison.individual')}</h4>
                            <p className="comparison-card-desc">{t('insoles.comparison.individual_desc')}</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default WhatAreInsoles;
